import { useQuery } from '@tanstack/react-query'
import { AlertCircle, Filter, Loader2 } from 'lucide-react'
import { useEffect, useMemo, useState, type ReactNode } from 'react'

import {
  reportsService,
  type CashRegisterFilterOption,
  type CashRegisterReportFilterOptions,
  type CashRegisterReportFilters,
  type CashRegisterReportResult,
  type CashRegisterReportType,
} from '../../../services/reports.service'
import { GlobalFilterBar } from '../../ui/GlobalFilterBar'
import { Button } from '../../ui/button'
import { GlobalPaginator } from '../../ui/global-paginator'
import { Input } from '../../ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table'
import { TableSkeleton } from '../../ui/table-skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs'

const DEFAULT_FILTERS: Record<
  CashRegisterReportType,
  CashRegisterReportFilters
> = {
  balances: {},
  closures: { result: 'all' },
}

const DEFAULT_PAGES: Record<CashRegisterReportType, number> = {
  balances: 1,
  closures: 1,
}

const REPORT_TABLE_HEAD_CLASS =
  'h-auto px-6 py-3 text-xs font-medium uppercase tracking-wider text-table-header-foreground'
const REPORT_TABLE_CELL_CLASS = 'px-6 py-4 text-table-foreground'

interface BalanceRow {
  cash_register: string
  branch: string
  current_balance: number
  movements_today: number
}

interface ClosureRow {
  date: string
  cash_register_branch: string
  folio: string
  user: string
  result: string
}

export interface AppliedCashRegisterReportContext {
  type: CashRegisterReportType
  filters: CashRegisterReportFilters
}

interface CajasTabProps {
  activeReport: CashRegisterReportType
  onActiveReportChange: (type: CashRegisterReportType) => void
  onAppliedContextChange: (context: AppliedCashRegisterReportContext) => void
}

export function CajasTab({
  activeReport,
  onActiveReportChange,
  onAppliedContextChange,
}: CajasTabProps) {
  const [draftFiltersByType, setDraftFiltersByType] = useState(DEFAULT_FILTERS)
  const [appliedFiltersByType, setAppliedFiltersByType] =
    useState(DEFAULT_FILTERS)
  const [pages, setPages] = useState(DEFAULT_PAGES)
  const optionsQuery = useQuery({
    queryKey: ['reports', 'cash-registers', 'filter-options'],
    queryFn: () => reportsService.getCashRegisterFilterOptions(),
    staleTime: 5 * 60 * 1000,
  })

  useEffect(() => {
    onAppliedContextChange({
      type: activeReport,
      filters: appliedFiltersByType[activeReport],
    })
  }, [activeReport, appliedFiltersByType, onAppliedContextChange])

  const handleReportChange = (value: string) => {
    const nextReport = value as CashRegisterReportType
    if (nextReport === activeReport) return

    setDraftFiltersByType((current) => ({
      ...current,
      [activeReport]: DEFAULT_FILTERS[activeReport],
      [nextReport]: DEFAULT_FILTERS[nextReport],
    }))
    setAppliedFiltersByType((current) => ({
      ...current,
      [activeReport]: DEFAULT_FILTERS[activeReport],
      [nextReport]: DEFAULT_FILTERS[nextReport],
    }))
    setPages((current) => ({
      ...current,
      [activeReport]: 1,
      [nextReport]: 1,
    }))
    onActiveReportChange(nextReport)
  }

  return (
    <TabsContent value="cajas" className="space-y-6">
      <Tabs
        value={activeReport}
        onValueChange={handleReportChange}
        className="space-y-5"
      >
        <TabsList className="h-auto w-full flex-wrap justify-start gap-1 bg-slate-100 p-1">
          <TabsTrigger value="balances" className="text-xs sm:text-sm">
            Saldos de Caja
          </TabsTrigger>
          <TabsTrigger value="closures" className="text-xs sm:text-sm">
            Cortes de Caja
          </TabsTrigger>
        </TabsList>

        <CashRegisterSubreport
          key={activeReport}
          type={activeReport}
          filters={draftFiltersByType[activeReport]}
          appliedFilters={appliedFiltersByType[activeReport]}
          page={pages[activeReport]}
          options={optionsQuery.data}
          optionsLoading={optionsQuery.isLoading}
          onFiltersChange={(filters) => {
            setDraftFiltersByType((current) => ({
              ...current,
              [activeReport]: filters,
            }))
          }}
          onApplyFilters={() => {
            setAppliedFiltersByType((current) => ({
              ...current,
              [activeReport]: draftFiltersByType[activeReport],
            }))
            setPages((current) => ({ ...current, [activeReport]: 1 }))
          }}
          onClearFilters={() => {
            setDraftFiltersByType((current) => ({
              ...current,
              [activeReport]: DEFAULT_FILTERS[activeReport],
            }))
            setAppliedFiltersByType((current) => ({
              ...current,
              [activeReport]: DEFAULT_FILTERS[activeReport],
            }))
            setPages((current) => ({ ...current, [activeReport]: 1 }))
          }}
          onPageChange={(page) =>
            setPages((current) => ({ ...current, [activeReport]: page }))
          }
        />
      </Tabs>
    </TabsContent>
  )
}

function CashRegisterSubreport({
  type,
  filters,
  appliedFilters,
  page,
  options,
  optionsLoading,
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
  onPageChange,
}: {
  type: CashRegisterReportType
  filters: CashRegisterReportFilters
  appliedFilters: CashRegisterReportFilters
  page: number
  options?: CashRegisterReportFilterOptions
  optionsLoading: boolean
  onFiltersChange: (filters: CashRegisterReportFilters) => void
  onApplyFilters: () => void
  onClearFilters: () => void
  onPageChange: (page: number) => void
}) {
  const queryFilters = useMemo(
    () => ({ ...appliedFilters, page }),
    [appliedFilters, page],
  )
  const reportQuery = useQuery({
    queryKey: ['reports', 'cash-registers', type, queryFilters],
    queryFn: () => reportsService.getCashRegisterReport(type, queryFilters),
    placeholderData: (previous) => previous,
  })
  const registerOptions = useMemo(() => {
    if (!filters.sucursal_id) return options?.cash_registers ?? []

    return (options?.cash_registers ?? []).filter(
      (register) => register.sucursal_id === filters.sucursal_id,
    )
  }, [filters.sucursal_id, options?.cash_registers])
  const supportsDates = type === 'closures'
  const activeFilters = hasActiveFilters(type, filters)

  return (
    <TabsContent value={type} className="space-y-5">
      <GlobalFilterBar
        show={supportsDates ? ['dates'] : []}
        values={{
          startDate: String(filters.date_from ?? ''),
          endDate: String(filters.date_to ?? ''),
        }}
        onChange={(changes) => {
          const nextFilters = { ...filters }
          if (changes.startDate !== undefined) {
            nextFilters.date_from = changes.startDate || undefined
          }
          if (changes.endDate !== undefined) {
            nextFilters.date_to = changes.endDate || undefined
          }
          onFiltersChange(nextFilters)
        }}
        onClearFilters={onClearFilters}
        hasActiveFilters={activeFilters}
      >
        <OptionsSelect
          label="Sucursal"
          value={filters.sucursal_id}
          options={options?.sucursals}
          onChange={(value) =>
            onFiltersChange({
              ...filters,
              sucursal_id: value === 'all' ? undefined : Number(value),
              cash_register_id: undefined,
            })
          }
        />
        <OptionsSelect
          label="Caja"
          value={filters.cash_register_id}
          options={registerOptions}
          optionLabel={registerOptionLabel}
          onChange={(value) =>
            onFiltersChange({
              ...filters,
              cash_register_id: value === 'all' ? undefined : Number(value),
            })
          }
        />
        {type === 'closures' && (
          <>
            <Field label="Folio">
              <Input
                aria-label="Folio"
                value={filters.folio ?? ''}
                placeholder="CORTE-000123"
                maxLength={32}
                onChange={(event) =>
                  onFiltersChange({
                    ...filters,
                    folio: event.target.value || undefined,
                  })
                }
              />
            </Field>
            <OptionsSelect
              label="Resultado"
              value={filters.result ?? 'all'}
              options={options?.results}
              allowAll={false}
              onChange={(value) =>
                onFiltersChange({
                  ...filters,
                  result: value as CashRegisterReportFilters['result'],
                })
              }
            />
            <OptionsSelect
              label="Usuario"
              value={filters.user_id}
              options={options?.users}
              onChange={(value) =>
                onFiltersChange({
                  ...filters,
                  user_id: value === 'all' ? undefined : Number(value),
                })
              }
            />
          </>
        )}
        {optionsLoading && (
          <span className="flex items-center gap-1 self-center text-xs text-slate-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Catálogos
          </span>
        )}
        <div className="flex items-end">
          <Button type="button" onClick={onApplyFilters}>
            <Filter className="mr-2 h-4 w-4" /> Aplicar filtros
          </Button>
        </div>
        {!supportsDates && activeFilters && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClearFilters}
            className="h-7 self-end rounded-full text-xs"
          >
            <Filter className="mr-1 h-3 w-3" /> Limpiar
          </Button>
        )}
      </GlobalFilterBar>

      <CashRegisterResults
        type={type}
        result={reportQuery.data}
        isLoading={reportQuery.isLoading}
        isFetching={reportQuery.isFetching}
        error={reportQuery.error}
        onPageChange={onPageChange}
      />
    </TabsContent>
  )
}

function CashRegisterResults({
  type,
  result,
  isLoading,
  isFetching,
  error,
  onPageChange,
}: {
  type: CashRegisterReportType
  result?: CashRegisterReportResult
  isLoading: boolean
  isFetching: boolean
  error: Error | null
  onPageChange: (page: number) => void
}) {
  if (isLoading) {
    return (
      <TableSkeleton
        headers={
          type === 'balances'
            ? ['Caja', 'Sucursal', 'Saldo actual', 'Movimientos hoy']
            : ['Fecha', 'Caja / Sucursal', 'Folio', 'Usuario', 'Resultado']
        }
        columns={
          type === 'balances'
            ? ['text', 'text', 'text', 'text']
            : ['text', 'text', 'text', 'text', 'badge']
        }
        rows={10}
      />
    )
  }
  if (error) {
    return (
      <ReportState
        icon={<AlertCircle className="h-6 w-6 text-red-500" />}
        message="No fue posible cargar el reporte. Intenta nuevamente."
      />
    )
  }
  if (!result || result.rows.length === 0) {
    return (
      <ReportState message="No hay registros para los filtros aplicados." />
    )
  }

  return (
    <div className="border-border bg-table-bg text-table-foreground overflow-hidden rounded-lg border shadow-sm">
      <div className="border-border flex items-center justify-between border-b px-6 py-4">
        <div>
          <h3 className="text-table-foreground font-semibold">Resultados</h3>
          <p className="text-muted-foreground text-xs">
            {result.pagination.total} registros filtrados
          </p>
        </div>
        {isFetching && (
          <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
        )}
      </div>

      <div className="overflow-x-auto">
        {type === 'balances' ? (
          <BalanceTable
            result={result as CashRegisterReportResult<BalanceRow>}
          />
        ) : (
          <ClosureTable
            result={result as CashRegisterReportResult<ClosureRow>}
          />
        )}
      </div>

      <GlobalPaginator
        totalItems={result.pagination.total}
        itemsPerPage={result.pagination.per_page}
        currentPage={result.pagination.current_page}
        from={result.pagination.from ?? 0}
        to={result.pagination.to ?? 0}
        onPageChange={onPageChange}
      />
    </div>
  )
}

function BalanceTable({
  result,
}: {
  result: CashRegisterReportResult<BalanceRow>
}) {
  return (
    <Table className="min-w-max">
      <TableHeader className="border-border bg-table-header-bg text-table-header-foreground border-b">
        <TableRow className="hover:bg-transparent">
          <TableHead className={REPORT_TABLE_HEAD_CLASS}>Caja</TableHead>
          <TableHead className={REPORT_TABLE_HEAD_CLASS}>Sucursal</TableHead>
          <TableHead className={`${REPORT_TABLE_HEAD_CLASS} text-right`}>
            Saldo actual
          </TableHead>
          <TableHead className={`${REPORT_TABLE_HEAD_CLASS} text-right`}>
            Movimientos hoy
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody className="[&_tr]:border-border">
        {result.rows.map((row) => (
          <TableRow
            key={`${row.branch}-${row.cash_register}`}
            className="hover:bg-table-row-hover"
          >
            <TableCell className={`${REPORT_TABLE_CELL_CLASS} font-medium`}>
              {row.cash_register}
            </TableCell>
            <TableCell className={REPORT_TABLE_CELL_CLASS}>
              {row.branch}
            </TableCell>
            <TableCell
              className={`${REPORT_TABLE_CELL_CLASS} text-right font-medium tabular-nums`}
            >
              {formatMoney(row.current_balance)}
            </TableCell>
            <TableCell
              className={`${REPORT_TABLE_CELL_CLASS} text-right font-medium tabular-nums`}
            >
              {row.movements_today}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
      <TableFooter className="border-border bg-table-header-bg text-table-foreground border-t font-semibold">
        <TableRow className="hover:bg-transparent">
          <TableCell className={`${REPORT_TABLE_CELL_CLASS} font-semibold`}>
            TOTAL GENERAL
          </TableCell>
          <TableCell className={`${REPORT_TABLE_CELL_CLASS} font-semibold`}>
            Cajas: {result.totals.record_count ?? 0}
          </TableCell>
          <TableCell
            className={`${REPORT_TABLE_CELL_CLASS} text-right font-semibold tabular-nums`}
          >
            {formatMoney(result.totals.current_balance)}
          </TableCell>
          <TableCell
            className={`${REPORT_TABLE_CELL_CLASS} text-right font-semibold tabular-nums`}
          >
            {result.totals.movements_today ?? 0}
          </TableCell>
        </TableRow>
      </TableFooter>
    </Table>
  )
}

function ClosureTable({
  result,
}: {
  result: CashRegisterReportResult<ClosureRow>
}) {
  return (
    <Table className="min-w-max">
      <TableHeader className="border-border bg-table-header-bg text-table-header-foreground border-b">
        <TableRow className="hover:bg-transparent">
          <TableHead className={REPORT_TABLE_HEAD_CLASS}>Fecha</TableHead>
          <TableHead className={REPORT_TABLE_HEAD_CLASS}>
            Caja / Sucursal
          </TableHead>
          <TableHead className={REPORT_TABLE_HEAD_CLASS}>Folio</TableHead>
          <TableHead className={REPORT_TABLE_HEAD_CLASS}>Usuario</TableHead>
          <TableHead className={REPORT_TABLE_HEAD_CLASS}>Resultado</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody className="[&_tr]:border-border">
        {result.rows.map((row) => (
          <TableRow key={row.folio} className="hover:bg-table-row-hover">
            <TableCell
              className={`${REPORT_TABLE_CELL_CLASS} whitespace-nowrap`}
            >
              {row.date}
            </TableCell>
            <TableCell className={`${REPORT_TABLE_CELL_CLASS} font-medium`}>
              {row.cash_register_branch}
            </TableCell>
            <TableCell className={REPORT_TABLE_CELL_CLASS}>
              {row.folio}
            </TableCell>
            <TableCell className={REPORT_TABLE_CELL_CLASS}>
              {row.user}
            </TableCell>
            <TableCell className={REPORT_TABLE_CELL_CLASS}>
              <ResultBadge result={row.result} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
      <TableFooter className="border-border bg-table-header-bg text-table-foreground border-t font-semibold">
        <TableRow className="hover:bg-transparent">
          <TableCell className={`${REPORT_TABLE_CELL_CLASS} font-semibold`}>
            TOTAL GENERAL
          </TableCell>
          <TableCell className={`${REPORT_TABLE_CELL_CLASS} font-semibold`}>
            Cortes: {result.totals.record_count ?? 0}
          </TableCell>
          <TableCell className={`${REPORT_TABLE_CELL_CLASS} font-semibold`}>
            Con diferencia: {result.totals.with_difference_count ?? 0}
          </TableCell>
          <TableCell className={`${REPORT_TABLE_CELL_CLASS} font-semibold`}>
            Negativos: {result.totals.negative_count ?? 0}; positivos:{' '}
            {result.totals.positive_count ?? 0}
          </TableCell>
          <TableCell className={`${REPORT_TABLE_CELL_CLASS} font-semibold`}>
            Sin diferencia: {result.totals.exact_count ?? 0}
          </TableCell>
        </TableRow>
      </TableFooter>
    </Table>
  )
}

function ResultBadge({ result }: { result: string }) {
  const className = result.includes('negativa')
    ? 'bg-red-100 text-red-700'
    : result.includes('positiva')
      ? 'bg-amber-100 text-amber-700'
      : 'bg-emerald-100 text-emerald-700'

  return (
    <span className={`rounded-full px-2 py-1 text-xs font-medium ${className}`}>
      {result}
    </span>
  )
}

function OptionsSelect({
  label,
  value,
  options = [],
  allowAll = true,
  optionLabel,
  onChange,
}: {
  label: string
  value: unknown
  options?: CashRegisterFilterOption[]
  allowAll?: boolean
  optionLabel?: (option: CashRegisterFilterOption) => string
  onChange: (value: string) => void
}) {
  const selected = value === undefined || value === null ? 'all' : String(value)

  return (
    <Field label={label}>
      <Select value={selected} onValueChange={onChange}>
        <SelectTrigger aria-label={label}>
          <SelectValue placeholder="Todos" />
        </SelectTrigger>
        <SelectContent>
          {allowAll && <SelectItem value="all">Todos</SelectItem>}
          {options.map((option) => {
            const optionValue = String(option.id ?? option.value)
            return (
              <SelectItem key={optionValue} value={optionValue}>
                {optionLabel?.(option) ??
                  option.name ??
                  option.label ??
                  optionValue}
              </SelectItem>
            )
          })}
        </SelectContent>
      </Select>
    </Field>
  )
}

function registerOptionLabel(option: CashRegisterFilterOption) {
  const name = option.code ? `${option.name} (${option.code})` : option.name
  const state = option.is_active === false ? ' · Inactiva' : ''

  return `${name ?? 'Caja'} / ${option.branch ?? 'Sucursal'}${state}`
}

function hasActiveFilters(
  type: CashRegisterReportType,
  filters: CashRegisterReportFilters,
) {
  const defaults = DEFAULT_FILTERS[type]
  const keys = new Set([...Object.keys(defaults), ...Object.keys(filters)])

  return Array.from(keys).some((key) => filters[key] !== defaults[key])
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-slate-700">{label}</label>
      {children}
    </div>
  )
}

function ReportState({ icon, message }: { icon?: ReactNode; message: string }) {
  return (
    <div className="flex min-h-52 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
      {icon}
      <span>{message}</span>
    </div>
  )
}

function formatMoney(value: string | number | null | undefined) {
  return Number(value ?? 0).toLocaleString('es-MX', {
    style: 'currency',
    currency: 'MXN',
  })
}
